import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Text,
  VStack,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Divider,
  Avatar,
  Image,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalHeader,
  Icon,
  Textarea,
  FormControl,
  FormLabel,
  useToast,
  Select,
  ModalFooter,
  Input,
  IconButton,
  Tooltip,
  Spinner,
} from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  Printer,
  Download,
  FileText,
  CheckCircle,
  XCircle,
  ShieldCheck,
  Edit2,
  DollarSign,
} from "lucide-react";
import { updateData, getDataById, getData } from "../../redux/actions/common.action";
import {
  ADMISSION_SECTION_KEYS,
  hasAdmissionSection,
  isApproverDesignation,
} from "../../utils/admissionAccess";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import AdmissionInvoice from "./AdmissionInvoice";

// --- Utility: Date Formatter ---
const formatDate = (dateStr: string) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ViewAdmission = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch: any = useDispatch();
  const toast = useToast();

  // Get admission data from Redux store or location state
  const { signinuser, singleAdmission, loading } = useSelector(
    (state: any) => state.common,
  );
  const { accounts } = useSelector((state: any) => state.common);

  // Prioritize fresh data from Redux; fallback to location state for immediate display
  const admissionData = singleAdmission || location.state;
  const admissionId = params.id || location.state?._id || singleAdmission?._id;

  // UI States
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isModeOpen,
    onOpen: onModeOpen,
    onClose: onModeClose,
  } = useDisclosure();
  const {
    isOpen: isPaymentOpen,
    onOpen: onPaymentOpen,
    onClose: onPaymentClose,
  } = useDisclosure();

  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [approvalRemark, setApprovalRemark] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [newPaymentMode, setNewPaymentMode] = useState(
    admissionData?.paymentMode || "Manual",
  );

  // Manual Payment States
  const [selectedEmi, setSelectedEmi] = useState<any>(null);
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentRemark, setPaymentRemark] = useState("");
  const [receivedAccountId, setReceivedAccountId] = useState("");

  // Invoice ref for print/download
  const invoiceRef = useRef<HTMLDivElement>(null);

  // Clear stale singleAdmission on mount and fetch fresh data
  useEffect(() => {
    // Clear old singleAdmission so stale data doesn't show
    dispatch({ type: "CLEAR_SINGLE_ADMISSION" });

    // Always fetch fresh data from API
    if (admissionId) {
      dispatch(getDataById(admissionId, "admission"));
    }
    dispatch(getData("account"));

    // Cleanup: clear singleAdmission when leaving the page
    return () => {
      dispatch({ type: "CLEAR_SINGLE_ADMISSION" });
    };
  }, [admissionId, dispatch]);

  // Update payment mode state when admission data changes
  useEffect(() => {
    if (admissionData?.paymentMode) {
      setNewPaymentMode(admissionData.paymentMode);
    }
  }, [admissionData?.paymentMode]);

  // --- Image Preview Logic ---
  const handleImagePreview = (src: string, title: string) => {
    setPreviewImage(src);
    setPreviewTitle(title);
    onOpen();
  };

  // --- Print Invoice ---
  const handlePrint = useCallback(() => {
    if (!invoiceRef.current) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const invoiceHTML = invoiceRef.current.innerHTML;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${admissionData?.name || "Admission"}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { background: white; }
          @media print {
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            @page { size: A4; margin: 0; }
          }
        </style>
      </head>
      <body>${invoiceHTML}</body>
      </html>
    `);

    printWindow.document.close();
    // Wait for images to load before printing
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    };
  }, [admissionData]);

  // --- Download Invoice as PDF ---
  const handleDownloadPDF = useCallback(async () => {
    if (!invoiceRef.current) return;

    toast({
      title: "Generating PDF...",
      status: "info",
      position: "top",
      duration: 2000,
    });

    try {
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      // If content exceeds one page, handle pagination
      const pageHeight = pdf.internal.pageSize.getHeight();
      let heightLeft = pdfHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `Invoice_${admissionData?.name?.replace(/\s+/g, "_") || "Admission"}_${admissionData?._id?.slice(-6) || ""}.pdf`;
      pdf.save(fileName);

      toast({
        title: "Invoice Downloaded!",
        description: fileName,
        status: "success",
        position: "top",
        duration: 3000,
      });
    } catch (error) {
      console.error("PDF generation error:", error);
      toast({
        title: "Download Failed",
        description: "Could not generate the invoice PDF.",
        status: "error",
        position: "top",
      });
    }
  }, [admissionData, toast]);

  // --- Financial Calculations ---
  const finance = useMemo(() => {
    if (!admissionData) return { totalPaid: 0, totalPending: 0 };
    const paidFromEmis =
      admissionData.installDetails
        ?.filter((e: any) => e.isPaid)
        .reduce((acc: number, curr: any) => acc + curr.amount, 0) || 0;
    const totalPaid = (admissionData.downPayment || 0) + paidFromEmis;
    const totalPending = (admissionData.totalCoursePrice || 0) - totalPaid;
    return { totalPaid, totalPending };
  }, [admissionData]);

  const isApproverOnly = useMemo(() => {
    if (!signinuser || signinuser.UserType !== 3) return false;
    return isApproverDesignation(signinuser);
  }, [signinuser]);

  const canMutateFinancials = useMemo(() => {
    if (!signinuser) return false;
    if (isApproverOnly) return false;
    return hasAdmissionSection(signinuser, ADMISSION_SECTION_KEYS.EDIT);
  }, [signinuser, isApproverOnly]);

  const canEditAdmissionRecord = useMemo(() => {
    if (!signinuser) return false;
    if (isApproverOnly) return false;
    return hasAdmissionSection(signinuser, ADMISSION_SECTION_KEYS.EDIT);
  }, [signinuser, isApproverOnly]);

  // --- Approval Access Logic ---
  const canApprove = useMemo(() => {
    if (!signinuser || !admissionData) return false;

    if (signinuser.UserType === 1) return true;

    // parentId is a plain string, not a populated object
    const parentId = typeof admissionData.parentId === "object"
      ? admissionData.parentId?._id
      : admissionData.parentId;

    // Check if user is Admin and is the parent/owner of this admission
    const isAdminOwner = signinuser.userId === parentId;

    // Check if user is the assigned Approver (designation / profile)
    const approverId = typeof admissionData.approverId === "object"
      ? admissionData.approverId?._id
      : admissionData.approverId;
    const isAssignedApprover = signinuser.userId === approverId;

    return isAdminOwner || isAssignedApprover;
  }, [signinuser, admissionData]);

  // --- Handle Status Update ---
  const handleStatusUpdate = async (newStatus: "Approved" | "Rejected") => {
    if (!approvalRemark.trim()) {
      toast({
        title: "Remark Required",
        description: "Please provide a remark for this action.",
        status: "warning",
        position: "top",
      });
      return;
    }

    setIsProcessing(true);
    const payload = {
      admStatus: newStatus,
      approvalRemark: approvalRemark,
      approvalDate: new Date(),
    };

    const res = await dispatch(
      updateData(admissionData._id, "admission", payload, navigate, toast),
    );

    if (res) {
      // Refresh the data instead of navigating away
      await dispatch(getDataById(admissionData._id, "admission"));
      setApprovalRemark("");
    }
    setIsProcessing(false);
  };

  // Logic: Can the current user change the payment mode?
  // Rules:
  //  - Approver cannot — they can only approve
  //  - Payment must not have started (any installment/full payment marked as paid)
  //  - Only the creator (createdBy) or counsellor (counsellorId) of the admission
  //    can change the mode; admins (UserType 1/2) are always allowed
  const canUpdatePaymentMode = useMemo(() => {
    if (!admissionData || !signinuser) return false;
    if (isApproverOnly) return false;
    const hasPaid = admissionData.installDetails?.some((emi: any) => emi.isPaid);
    if (hasPaid) return false;
    if (signinuser.UserType === 1 || signinuser.UserType === 2) return true;
    const createdBy =
      typeof admissionData.createdBy === "object"
        ? admissionData.createdBy?._id
        : admissionData.createdBy;
    const counsellorId =
      typeof admissionData.counsellorId === "object"
        ? admissionData.counsellorId?._id
        : admissionData.counsellorId;
    return (
      String(signinuser.userId) === String(createdBy) ||
      String(signinuser.userId) === String(counsellorId)
    );
  }, [admissionData, signinuser, isApproverOnly]);

  // Handle Update Payment Mode
  const handleUpdatePaymentMode = async () => {
    if (!newPaymentMode) {
      toast({
        title: "Payment Mode Required",
        description: "Please select a payment mode.",
        status: "warning",
        position: "top",
      });
      return;
    }

    setIsProcessing(true);
    const payload = { paymentMode: newPaymentMode };

    try {
      const res = await dispatch(
        updateData(
          admissionData._id,
          "admission/update-payment-mode",
          payload,
          navigate,
          toast,
        ),
      );

      if (res) {
        // Refresh the admission data from server
        await dispatch(getDataById(admissionData._id, "admission"));
        onModeClose();
      }
    } catch (error) {
      console.error("Error updating payment mode:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Handle Manual Payment ---
  const handleOpenPaymentModal = (emi: any) => {
    setSelectedEmi(emi);
    setPaymentDate("");
    setPaymentRemark("");
    setReceivedAccountId(admissionData?.paymentAccount?._id || "");
    onPaymentOpen();
  };

  const handleUpdateManualPayment = async () => {
    if (!paymentDate) {
      toast({
        title: "Payment Date Required",
        description: "Please select the payment date and time.",
        status: "warning",
        position: "top",
      });
      return;
    }

    if (!paymentRemark.trim()) {
      toast({
        title: "Remark Required",
        description: "Please provide a remark for this payment.",
        status: "warning",
        position: "top",
      });
      return;
    }

    if (!receivedAccountId) {
      toast({
        title: "Bank Account Required",
        description: "Please select the bank account where you received the payment.",
        status: "warning",
        position: "top",
      });
      return;
    }

    setIsProcessing(true);
    const payload: any = {
      paymentDate: new Date(paymentDate).toISOString(),
      remark: paymentRemark,
      paymentAccountId: receivedAccountId,
    };
    // Only include emiId for installment type (full payment sends no emiId)
    if (selectedEmi?._id) {
      payload.emiId = selectedEmi._id;
    }

    try {
      const res = await dispatch(
        updateData(
          admissionData._id,
          "admission/update-manual-payment",
          payload,
          navigate,
          toast,
        ),
      );

      if (res) {
        // Refresh the admission data from server
        await dispatch(getDataById(admissionData._id, "admission"));
        onPaymentClose();
        setSelectedEmi(null);
        setPaymentDate("");
        setPaymentRemark("");
      }
    } catch (error) {
      console.error("Error updating manual payment:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Loading state
  if (loading && !admissionData) {
    return (
      <Box p={10} textAlign="center">
        <Spinner size="xl" color="blue.500" />
        <Text mt={4} color="gray.600">
          Loading admission details...
        </Text>
      </Box>
    );
  }

  // No data state
  if (!admissionData) {
    return (
      <Box p={10} textAlign="center">
        <Text fontSize="xl" color="red.500">
          No Admission Data Found.
        </Text>
        <Button mt={4} onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  const InfoRow = ({
    label,
    value,
    isBold = false,
    color = "gray.800",
  }: any) => (
    <HStack
      justify="space-between"
      w="full"
      py={2}
      borderBottom="1px solid"
      borderColor="gray.50"
    >
      <Text
        fontSize="xs"
        color="gray.500"
        fontWeight="600"
        textTransform="uppercase"
      >
        {label}
      </Text>
      <Text fontSize="sm" fontWeight={isBold ? "700" : "500"} color={color}>
        {value || "N/A"}
      </Text>
    </HStack>
  );

  return (
    <Box p={{ base: 2, md: 6 }} bg="gray.50" minH="100vh">
      <Container maxW="container.xl" p={0}>
        {/* Header Actions */}
        <Flex justify="space-between" align="center" mb={6}>
          <Button
            leftIcon={<ArrowLeft size={18} />}
            variant="ghost"
            onClick={() => navigate(-1)}
          >
            Back to Admissions
          </Button>
          <HStack spacing={3}>
            {admissionData.admStatus === "Rejected" && canEditAdmissionRecord && (
              <Button
                leftIcon={<Edit2 size={18} />}
                colorScheme="orange"
                variant="solid"
                onClick={() =>
                  navigate("/admission-form", { state: admissionData })
                }
              >
                Edit Admission
              </Button>
            )}
            <Button
              leftIcon={<Printer size={18} />}
              variant="outline"
              bg="white"
              onClick={handlePrint}
            >
              Print
            </Button>
            <Button
              leftIcon={<Download size={18} />}
              colorScheme="blue"
              onClick={handleDownloadPDF}
            >
              Export Invoice
            </Button>
          </HStack>
        </Flex>

        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
          {/* LEFT COLUMN: Profile & Documents */}
          <VStack spacing={6} align="stretch">
            <Card variant="outline" boxShadow="sm" overflow="hidden">
              <Box bg="blue.600" h="80px" />
              <VStack spacing={3} mt="-40px" pb={6}>
                <Avatar
                  size="2xl"
                  name={admissionData.name}
                  src={admissionData.profilePic}
                  border="4px solid white"
                  boxShadow="md"
                />
                <VStack spacing={0}>
                  <Heading size="md">{admissionData.name}</Heading>
                  <Text color="gray.500" fontSize="sm">
                    Lead ID:{" "}
                    {typeof admissionData?.leadId === 'string'
                      ? admissionData.leadId.slice(-6).toUpperCase()
                      : admissionData?.leadId?._id
                      ? String(admissionData.leadId._id).slice(-6).toUpperCase()
                      : "N/A"}
                  </Text>
                </VStack>
                <Badge
                  colorScheme={
                    admissionData.admStatus === "Approved" ? "green" : "orange"
                  }
                  px={4}
                  py={1}
                  borderRadius="full"
                >
                  {admissionData.admStatus?.toUpperCase() || "PENDING"}
                </Badge>
              </VStack>
              <Divider />
              <CardBody>
                <VStack spacing={1}>
                  <InfoRow label="Email" value={admissionData.email} />
                  <InfoRow label="Phone" value={admissionData.mobileNo} />
                  <InfoRow
                    label="Branch"
                    value={admissionData.branchId?.BranchName}
                  />
                  <InfoRow
                    label="Counsellor"
                    value={admissionData.counsellorId?.Name}
                  />
                  <InfoRow
                    label="Approver"
                    value={admissionData.approverId?.Name}
                  />
                  <InfoRow
                    label="Date"
                    value={formatDate(admissionData.admDate)}
                  />
                </VStack>
              </CardBody>
            </Card>

            {/* Documents Card */}
            <Card variant="outline" boxShadow="sm">
              <CardHeader
                pb={2}
                borderBottom="1px solid"
                borderColor="gray.100"
              >
                <HStack>
                  <Icon as={FileText} color="blue.500" />
                  <Heading size="xs">KYC DOCUMENTS</Heading>
                </HStack>
              </CardHeader>
              <CardBody>
                <SimpleGrid columns={2} spacing={3}>
                  {[
                    { key: "frontAadhar", label: "Aadhar Front" },
                    { key: "backAadhar", label: "Aadhar Back" },
                    { key: "panCard", label: "PAN Card" },
                  ].map(
                    (doc) =>
                      admissionData[doc.key] && (
                        <Box key={doc.key} textAlign="center">
                          <Box
                            border="1px solid #eee"
                            borderRadius="md"
                            overflow="hidden"
                            cursor="pointer"
                            onClick={() =>
                              handleImagePreview(
                                admissionData[doc.key],
                                doc.label,
                              )
                            }
                            _hover={{ opacity: 0.8 }}
                          >
                            <Image
                              src={admissionData[doc.key]}
                              h="80px"
                              w="full"
                              objectFit="cover"
                              fallbackSrc="https://via.placeholder.com/150"
                            />
                          </Box>
                          <Text
                            fontSize="10px"
                            mt={1}
                            fontWeight="bold"
                            color="gray.500"
                          >
                            {doc.label}
                          </Text>
                        </Box>
                      ),
                  )}
                </SimpleGrid>
              </CardBody>
            </Card>
          </VStack>

          {/* RIGHT COLUMN: Details & Approval */}
          <VStack spacing={6} gridColumn={{ lg: "span 2" }} align="stretch">
            {/* Financial Cards */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Card variant="outline" bg="white">
                <CardBody py={4}>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color="gray.500" fontWeight="bold">
                      COURSE FEE
                    </Text>
                    <Text fontSize="xl" fontWeight="800">
                      ₹{admissionData.totalCoursePrice}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
              <Card variant="outline" bg="white">
                <CardBody py={4}>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color="gray.500" fontWeight="bold">
                      TOTAL PAID
                    </Text>
                    <Text fontSize="xl" fontWeight="800" color="green.600">
                      ₹{finance.totalPaid}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
              <Card variant="outline" bg="white">
                <CardBody py={4}>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color="gray.500" fontWeight="bold">
                      PENDING
                    </Text>
                    <Text fontSize="xl" fontWeight="800" color="red.600">
                      ₹{finance.totalPending}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>

            {/* Payment Mode Section */}
            <Card variant="outline" bg="white">
              <CardBody py={4}>
                <Flex justify="space-between" align="center">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color="gray.500" fontWeight="bold">
                      CURRENT PAYMENT MODE
                    </Text>
                    <Badge colorScheme="blue" mt={1}>
                      {admissionData.paymentMode}
                    </Badge>
                  </VStack>

                  {canUpdatePaymentMode ? (
                    <Button
                      size="sm"
                      leftIcon={<Edit2 size={14} />}
                      variant="outline"
                      colorScheme="blue"
                      onClick={onModeOpen}
                    >
                      Change Mode
                    </Button>
                  ) : admissionData.installDetails?.some((e: any) => e.isPaid) ? (
                    <Text fontSize="xs" color="orange.500" fontWeight="medium">
                      🔒 Locked (payment started)
                    </Text>
                  ) : (
                    <Text fontSize="xs" color="gray.400" fontWeight="medium">
                      No permission
                    </Text>
                  )}
                </Flex>
              </CardBody>
            </Card>

            {/* Approval Action Section */}
            {canApprove && admissionData.admStatus === "Pending" && (
              <Card
                variant="outline"
                border="2px solid"
                borderColor="blue.100"
                bg="blue.50"
              >
                <CardHeader pb={0}>
                  <HStack>
                    <Icon as={ShieldCheck} color="blue.600" />
                    <Heading size="sm">Admission Approval Action</Heading>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <FormControl isRequired>
                      <FormLabel fontSize="xs" fontWeight="bold">
                        Approval / Rejection Remark
                      </FormLabel>
                      <Textarea
                        bg="white"
                        placeholder="Enter remark here..."
                        value={approvalRemark}
                        onChange={(e) => setApprovalRemark(e.target.value)}
                      />
                    </FormControl>
                    <HStack spacing={4}>
                      <Button
                        leftIcon={<CheckCircle size={18} />}
                        colorScheme="green"
                        flex={1}
                        isLoading={isProcessing}
                        onClick={() => handleStatusUpdate("Approved")}
                      >
                        Approve
                      </Button>
                      <Button
                        leftIcon={<XCircle size={18} />}
                        colorScheme="red"
                        variant="outline"
                        flex={1}
                        isLoading={isProcessing}
                        onClick={() => handleStatusUpdate("Rejected")}
                      >
                        Reject
                      </Button>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            )}

            {/* History Card */}
            {admissionData.admStatus !== "Pending" && (
              <Card variant="outline" bg="gray.50">
                <CardBody p={4}>
                  <VStack align="start" spacing={1}>
                    <Text fontSize="xs" fontWeight="bold" color="gray.500">
                      APPROVAL REMARK:
                    </Text>
                    <Text fontSize="sm" fontWeight="600">
                      {admissionData.approvalRemark || "No remark provided"}
                    </Text>
                    <Text fontSize="xs" color="gray.400">
                      Processed on:{" "}
                      {formatDate(
                        admissionData.approvalDate || admissionData.updatedAt,
                      )}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            )}

            {/* Full Payment Section */}
            {admissionData.paymentType === "Full" && admissionData.admStatus === "Approved" && (() => {
              // Find the seeded "Full Payment" entry (may not exist for very old records)
              const fullEntry = admissionData.installDetails?.find(
                (e: any) => e.label === "Full Payment",
              );
              const isPaid = fullEntry?.isPaid === true;
              return (
                <Card variant="outline" boxShadow="sm">
                  <CardHeader bg="gray.50" borderBottom="1px solid #eee">
                    <Heading size="xs">FULL PAYMENT</Heading>
                  </CardHeader>
                  <CardBody>
                    <Flex align="center" justify="space-between" wrap="wrap" gap={3}>
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm" color="gray.600">Total Course Fee</Text>
                        <Text fontSize="xl" fontWeight="800" color="gray.800">
                          ₹{admissionData.totalCoursePrice}
                        </Text>
                        {isPaid ? (
                          <Text fontSize="xs" color="green.600">
                            Paid on {formatDate(fullEntry.paidDate)}
                            {fullEntry.remark ? ` · ${fullEntry.remark}` : ""}
                          </Text>
                        ) : (
                          <Text fontSize="xs" color="orange.500">
                            Awaiting payment
                          </Text>
                        )}
                      </VStack>
                      <HStack spacing={3}>
                        <Badge
                          colorScheme={isPaid ? "green" : "orange"}
                          fontSize="sm"
                          px={3}
                          py={1}
                          borderRadius="full"
                        >
                          {isPaid ? "PAID" : "PENDING"}
                        </Badge>
                        {!isPaid && canMutateFinancials && (
                          <Tooltip label="Record Manual Payment">
                            <IconButton
                              aria-label="Record Full Payment"
                              icon={<DollarSign size={16} />}
                              size="sm"
                              colorScheme="green"
                              variant="outline"
                              onClick={() =>
                                handleOpenPaymentModal({
                                  label: "Full Payment",
                                  amount: admissionData.totalCoursePrice,
                                  _id: fullEntry?._id || null,
                                })
                              }
                            />
                          </Tooltip>
                        )}
                      </HStack>
                    </Flex>
                  </CardBody>
                </Card>
              );
            })()}

            {/* EMI Table */}
            {admissionData.paymentType === "Installment" && (
              <Card variant="outline" boxShadow="sm">
                <CardHeader bg="gray.50" borderBottom="1px solid #eee">
                  <Heading size="xs">EMI INSTALLMENT SCHEDULE</Heading>
                </CardHeader>
                <CardBody p={0}>
                  <Table variant="simple" size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Label</Th>
                        <Th>Due Date</Th>
                        <Th>Amount</Th>
                        <Th>Status</Th>
                        <Th>Remark</Th>
                        {admissionData.admStatus === "Approved" && (
                          <Th>Action</Th>
                        )}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {admissionData.installDetails?.map(
                        (emi: any, idx: number) => (
                          <Tr key={idx}>
                            <Td fontWeight="bold">{emi.label}</Td>
                            <Td>{formatDate(emi.installDate)}</Td>
                            <Td fontWeight="bold">₹{emi.amount}</Td>
                            <Td>
                              <Badge colorScheme={emi.isPaid ? "green" : "red"}>
                                {emi.isPaid ? "PAID" : "PENDING"}
                              </Badge>
                            </Td>
                            <Td fontSize="xs">{emi.remark || "-"}</Td>
                            {admissionData.admStatus === "Approved" && (
                              <Td>
                                {!emi.isPaid && canMutateFinancials && (
                                  <Tooltip
                                    label={
                                      admissionData.paymentMode === "AUTO_PAYPAL" ||
                                      admissionData.paymentMode === "AUTO_RAZORPAY"
                                        ? "Manual override (Auto payment mode)"
                                        : "Mark as Paid"
                                    }
                                  >
                                    <IconButton
                                      aria-label="Update Payment"
                                      icon={<DollarSign size={16} />}
                                      size="sm"
                                      colorScheme={
                                        admissionData.paymentMode === "AUTO_PAYPAL" ||
                                        admissionData.paymentMode === "AUTO_RAZORPAY"
                                          ? "orange"
                                          : "green"
                                      }
                                      variant="outline"
                                      onClick={() => handleOpenPaymentModal(emi)}
                                    />
                                  </Tooltip>
                                )}
                                {!emi.isPaid && !canMutateFinancials && (
                                  <Text fontSize="xs" color="gray.500">
                                    —
                                  </Text>
                                )}
                                {emi.isPaid && (
                                  <Text fontSize="xs" color="green.600">
                                    Paid on {formatDate(emi.paidDate)}
                                  </Text>
                                )}
                              </Td>
                            )}
                          </Tr>
                        ),
                      )}
                    </Tbody>
                  </Table>
                </CardBody>
              </Card>
            )}
          </VStack>
        </SimpleGrid>
      </Container>

      {/* Image Preview Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(5px)" />
        <ModalContent borderRadius="xl" overflow="hidden">
          <ModalHeader py={3} px={4} bg="white" borderBottom="1px solid #eee">
            <Flex justify="space-between" align="center">
              <Text fontWeight="bold" fontSize="sm">
                {previewTitle}
              </Text>
              <ModalCloseButton position="static" />
            </Flex>
          </ModalHeader>
          <ModalBody p={0} bg="gray.100">
            <Flex justify="center" align="center" minH="300px">
              <Image src={previewImage} maxH="80vh" objectFit="contain" />
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Update Payment Mode Modal */}
      <Modal isOpen={isModeOpen} onClose={onModeClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Payment Mode</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={4} color="gray.600">
              Change the payment gateway for this student. This is only allowed
              because no installments have been paid yet.
            </Text>
            <FormControl>
              <FormLabel>Select New Mode</FormLabel>
              <Select
                value={newPaymentMode}
                onChange={(e) => setNewPaymentMode(e.target.value)}
              >
                <option value="Manual">Manual</option>
                <option value="auto_razorpay">Razorpay</option>
                <option value="auto_paypal">PayPal</option>
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onModeClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdatePaymentMode}
              isLoading={isProcessing}
            >
              Update Mode
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Manual Payment Update Modal */}
      <Modal isOpen={isPaymentOpen} onClose={onPaymentClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Record Manual Payment</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box
                p={3}
                bg="blue.50"
                borderRadius="md"
                border="1px solid"
                borderColor="blue.200"
              >
                <Text fontSize="sm" fontWeight="bold" color="blue.700">
                  {selectedEmi?.label}
                </Text>
                <Text fontSize="lg" fontWeight="800" color="blue.900">
                  ₹{selectedEmi?.amount}
                </Text>
                <Text fontSize="xs" color="gray.600">
                  Due: {formatDate(selectedEmi?.installDate)}
                </Text>
              </Box>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold">
                  Received In Bank Account
                </FormLabel>
                <Select
                  placeholder="Select bank account"
                  value={receivedAccountId}
                  onChange={(e) => setReceivedAccountId(e.target.value)}
                >
                  {(accounts || [])
                    ?.filter((a: any) => !a?.isDeleted)
                    ?.map((acc: any) => (
                      <option key={acc._id} value={acc._id}>
                        {acc.bankName} • {String(acc.accountNo || "").slice(-4)}
                        {acc.upiId ? ` • UPI: ${acc.upiId}` : ""}
                      </option>
                    ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold">
                  Payment Date & Time
                </FormLabel>
                <Input
                  type="datetime-local"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  max={new Date().toISOString().slice(0, 16)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold">
                  Remark <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <Textarea
                  placeholder="Add notes about this payment..."
                  value={paymentRemark}
                  onChange={(e) => setPaymentRemark(e.target.value)}
                  rows={3}
                  isInvalid={!paymentRemark.trim()}
                />
              </FormControl>

              <Box
                p={3}
                bg="yellow.50"
                borderRadius="md"
                border="1px solid"
                borderColor="yellow.200"
              >
                <Text fontSize="xs" color="gray.700">
                  <strong>Note:</strong> An email confirmation will be sent to
                  the student after recording this payment.
                </Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPaymentClose} isDisabled={isProcessing}>
              Cancel
            </Button>
            <Button
              colorScheme="green"
              onClick={handleUpdateManualPayment}
              isLoading={isProcessing}
              leftIcon={<CheckCircle size={18} />}
            >
              Record Payment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Hidden Invoice for Print / PDF - off-screen */}
      <Box
        position="fixed"
        left="-9999px"
        top="0"
        zIndex={-1}
        overflow="hidden"
      >
        <AdmissionInvoice
          ref={invoiceRef}
          admissionData={admissionData}
          signinuser={signinuser}
        />
      </Box>
    </Box>
  );
};

export default ViewAdmission;
